<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      {
        expanded:
          !isVerticalMenuCollapsed ||
          (isVerticalMenuCollapsed && isMouseHovered),
      },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light',
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">
          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link class="navbar-brand" to="/">
              <span class="brand-logo">
                <b-img :src="appLogoImage" alt="logo" />
              </span>
              <h2 class="brand-text">
                {{ appName }}
              </h2>
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div :class="{ 'd-block': shallShadowBottom }" class="shadow-bottom" />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="
        (evt) => {
          shallShadowBottom = evt.srcElement.scrollTop > 0;
        }
      "
    >
      <vertical-nav-menu-items
        :items="navMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import navMenuItems from "@/navigation/vertical";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { BLink, BImg } from "bootstrap-vue";
import { provide, computed, ref } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";
import { $themeConfig } from "@themeConfig";
import VerticalNavMenuItems from "./components/vertical-nav-menu-items/VerticalNavMenuItems.vue";
import useVerticalNavMenu from "./useVerticalNavMenu";
import store from '@/store/index'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const { role, isComplaintManager, permissions } = store.state.userStore.userData;
    const navMenuItemsDeepCopy = JSON.parse(JSON.stringify(navMenuItems || []))

    const filterAndMap = (item) => {
      // Ítems migrados a permission_default (opt-in, vía moduleKey): la visibilidad se
      // decide por userData.permissions en vez de por `roles`. Los flags de cliente
      // (commitment_functionality, excludedPages, etc.) siguen aplicando después de esto,
      // sin condicionarse a ninguno de los dos mecanismos.
      // Si el propio ítem tiene moduleKey, gatea el grupo entero primero (ej. "Risks",
      // "Calendar", "Complaint Channel") — sin retornar todavía si pasa, porque además puede
      // tener hijos con su propio moduleKey (ver bloque siguiente).
      if (item.moduleKey && !permissions?.[item.moduleKey]?.view?.allowed) return false;

      // Hijos migrados individualmente (moduleKey propio) — filtra cada uno; los que no
      // tienen moduleKey pasan sin restricción (mismo criterio "sin declarar = abierto" que
      // el resto del sistema). Si ninguno sobrevive, se oculta el grupo entero; si el `route`
      // propio del grupo apuntaba a un hijo que quedó filtrado, se saca (mismo patrón ya
      // usado más abajo para `organization-complaint-list`).
      if (item.hasOwnProperty("children") && item.children.some((child) => child.moduleKey)) {
        const haveAccessToChilds = item.children.filter((child) => (
          child.moduleKey ? permissions?.[child.moduleKey]?.view?.allowed : true
        ));
        if (!haveAccessToChilds.length) return false;
        const filtered = { ...item, children: haveAccessToChilds };
        if (filtered.route && !haveAccessToChilds.some((child) => child.route === filtered.route)) {
          delete filtered.route;
        }
        return filtered;
      }
      if (item.moduleKey) return item;
      if (item.roles) {
        const roles = Object.keys(item.roles);
        if (item.hasOwnProperty("children")) {
          const haveAccessToChilds = item?.children?.filter((child) => {
            return child.roles.hasOwnProperty(role);
          });

          if (!roles.includes(role) || !haveAccessToChilds.length) return false;
          else return { ...item, children: haveAccessToChilds };
        } else if (!roles.includes(role)) return false;
      }
      return item;
    };
    const filteredNavItems = navMenuItemsDeepCopy.filter(filterAndMap).map(filterAndMap);

    // Personalize vertical nav menu for different clients
    const clientData = JSON.parse(localStorage.getItem("clientData") || '{}')
    const commitmentFunctionality = clientData.commitment_functionality
    const pcTeamDefaultView = clientData.default_view_pc_team
    const excludePCFunctionality = clientData.exclude_pc_functionality
    const excludePCTeamModule = clientData.exclude_pc_team_module
    const excludedPages = clientData.excludedPages || []
    // const employeeExcludedPages = [ "habit-general-dashboard", "habit-confirmation-dashboard", "habit-daily-dashboard", "habit-adherence-dashboard", "habit-behaviour-dashboard", "habit-commitment-dashboard", "organization-process-list", "organization-control-list", "organization-behaviour-list", "apps-calendar", "organization-confirmation-planning-list", "misc-coming-soon", "organization-lobby-list", "organization-worker-list", "organization-location-list", "organization-role-list", "organization-crime-list", "pages-account-setting" ]
    // const excludedPages = role === 'employee' ? Array.from(new Set([...clientExcludedPages, ...employeeExcludedPages])) : clientExcludedPages
    let excludeDashboards = false

    for (let i = 0; i < filteredNavItems.length; i++) {
      const e = filteredNavItems[i];

      // TODO: Improve this logic because it relies on header names "PC Dashboards" and "DD Dashboards" to not change over time
      // Exclude PC Dashboards and/or DD Dashboards section/s if applicable
      if (excludePCFunctionality || commitmentFunctionality) {
        if ((excludePCFunctionality && e.header === "PC Dashboards") || (commitmentFunctionality && e.header === "DD Dashboards")) {
          excludeDashboards = true
        } else if (excludeDashboards && e.header) {
          excludeDashboards = false
        }
        if (excludeDashboards) {
          filteredNavItems.splice(i, 1)
          i--
          continue
        }
      }

      // Personalize or exclude item based on route
      if (e.children) {
        for (let j = 0; j < e.children.length; j++) {
          const child = e.children[j];
          switch (child.route) {
            case 'organization-process-list':
              filteredNavItems[i].children[j].title = commitmentFunctionality ? 'Confirmations List' : 'List'
              break;
            case 'apps-pc-team':
              if (excludePCTeamModule) {
                filteredNavItems.splice(i, 1)
                i--
              }
              break;
            case 'habit-adherence-dashboard':
              if (commitmentFunctionality) {
                filteredNavItems.splice(i, 1)
                i--
              }
              break;
            case 'organization-worker-list':
              if (commitmentFunctionality) {
                filteredNavItems.splice(i, 1)
                i--
              }
              break;
            case 'habit-daily-dashboard':
              if (commitmentFunctionality) {
                filteredNavItems[i].children.splice(j, 1)
                j--
              }
              break;
            case 'organization-pending-confirmations':
              if (commitmentFunctionality) {
                filteredNavItems[i].children.splice(j, 1)
                j--
              }
              break;
            case 'complaint-follow-up':
              child.route = { name: 'complaint-follow-up', params: { clientPublicId: clientData.publicId } }
              break;
            case 'arsopbl-follow-up':
              child.route = { name: 'arsopbl-follow-up', params: { clientPublicId: clientData.publicId } }
              break;
            case 'organization-complaint-list':
              if (!isComplaintManager) {
                filteredNavItems[i].children.splice(j, 1)
                delete filteredNavItems[i].route    // to avoid default route destination when the nav option is clicked
                j--
              }
              break;
            default:
              if (child.route?.name === 'complaint-follow-up' || child.route?.name === 'arsopbl-follow-up') {
                child.route.params.clientPublicId = clientData.publicId
              }
              break;
          }
        }
      } else {
        switch (e.route) {
          case 'apps-calendar':
            filteredNavItems[i].title = commitmentFunctionality ? 'Schedule' : 'Calendar'
            if (pcTeamDefaultView || excludePCFunctionality) {
              filteredNavItems.splice(i, 1)
              i--
            }
            break;
          case 'apps-todo':
            filteredNavItems[i].title = commitmentFunctionality ? 'Commitments' : 'Continuous Improvement'
            break;
          case 'habit-improvement-dashboard':
            filteredNavItems[i].title = commitmentFunctionality ? 'Commitments Dashboard' : 'Improvement Opportunities'
            break;
          case 'habit-meeting-new':
            if (commitmentFunctionality) {
              filteredNavItems.splice(i, 1)
              i--
            }
            break;
          case 'apps-problem-solving':
            if (!commitmentFunctionality) {
              filteredNavItems.splice(i, 1)
              i--
            }
            break;
          default:
            break;
        }
      }
    }

    // Function to filter navigation items based on excludedPages
    const filterNavigation = (items, excludedPages) => {
      return items
        .map(item => {
          // If the item has a `route` check if it should be excluded
          if (item.route && excludedPages.includes(item.route?.name || item.route)) {
            return null;
          }

          // Create a shallow copy of the item to avoid mutating the original array
          const newItem = { ...item };

          // If the item has children, recursively filter them
          if (newItem.children) {
            newItem.children = filterNavigation(newItem.children, excludedPages);
          }

          return newItem;
        })
        .filter(item => item !== null);
    }

    // If the client has pages to be excluded, filter the nav items again
    const navItemsWithExcludedPages = !excludedPages?.length
      ? filteredNavItems
      : filterNavigation(filteredNavItems, excludedPages)

    const removeUnnecessaryHeaders = (array) => {
      return array.filter((item, index) => {
        // Keep the item if it does not have a header property
        if (!item.header) return true;

        // Check if the next element has a title property
        const nextElement = array[index + 1];
        return nextElement && nextElement.title;
      });
    }

    const finalNavItems = removeUnnecessaryHeaders(navItemsWithExcludedPages)

    store.commit('userStore/updatePermissions', finalNavItems);
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props);

    const { skin } = useAppConfig();

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false);

    provide("isMouseHovered", isMouseHovered);

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    };

    const collapseTogglerIconFeather = computed(() =>
      collapseTogglerIcon.value === "unpinned" ? "CircleIcon" : "DiscIcon"
    );

    // App Name
    const { appName, appLogoImage } = $themeConfig.app;

    return {
      navMenuItems: finalNavItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
    };
  },
};
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>